import { getCartCookie } from "@/utils/cart/getCartCookie";
import { claimPurchaseTracking } from "@/utils/claimPurchaseTracking";
import {
  gTagAddPaymentInfo,
  gTagAddShippingInfo,
  gTagAddToCart,
  gTagBeginCheckout,
  gTagCheckoutDenied,
  gTagEmailRegistration,
  gTagPurchaseV2,
  gTagRemoveFromCart,
  gTagSelectListItem,
  gTagViewCart,
  gTagViewItem,
  gTagViewItemList,
} from "@/utils/googleTags";
import { sendVoyadoOnsiteCheckout } from "@/utils/voyadoOnsite";
import {
  sendVoyadoCartChangeEvent,
  sendVoyadoEmptyCartEvent,
  sendVoyadoProductViewEvent,
  type VoyadoTrackableCart,
} from "@/utils/voyadoTracking";
import { useMarket } from "@app/_context";
import elevateClient from "@server/elevate";
import type { Cart, CartItem, GetOrderQuery, Page } from "@server/gql/graphql";
import type { IncomingItem } from "@server/utils/cart";
import { useCallback } from "react";
import type { Product, Variant } from "types";

export default function useEvents() {
  const market = useMarket().state.market;
  const addPaymentInfo = useCallback((cart: Cart) => {
    void gTagAddPaymentInfo(cart);
  }, []);
  const addShippingInfo = useCallback((cart: Cart) => {
    void gTagAddShippingInfo(cart);
  }, []);
  const addToCart = useCallback(
    (items: IncomingItem[], cart: Cart) => {
      void gTagAddToCart(items, cart);
      void sendVoyadoCartChangeEvent({
        cart,
        market,
      });
      items.forEach(
        ({ item }) => void elevateClient(market).notify.addToCart(item.ticket),
      );
    },
    [market],
  );
  const beginCheckout = useCallback((cart: Cart) => {
    void gTagBeginCheckout(cart);
  }, []);
  const clickItem = useCallback(
    (item: Variant | Product) => {
      void gTagSelectListItem(item, market.currency);
      void elevateClient(market).notify.click(item.ticket);
    },
    [market],
  );
  const emailRegistration = useCallback((email: string) => {
    void gTagEmailRegistration(email);
  }, []);
  const purchase = useCallback(
    (order: GetOrderQuery["order"], cartRef?: string) => {
      if (!order?.orderId) return;
      if (!claimPurchaseTracking(order.orderId)) return;

      void gTagPurchaseV2(order);
      void sendVoyadoEmptyCartEvent(cartRef ?? getCartCookie()?.id);
      void sendVoyadoOnsiteCheckout(order);
    },
    [],
  );
  const removeFromCart = useCallback(
    (item: CartItem, cart: Cart) => {
      void gTagRemoveFromCart(item, cart);
      void sendVoyadoCartChangeEvent({
        cart,
        market,
      });
    },
    [market],
  );
  const updateCart = useCallback(
    (cart: VoyadoTrackableCart) => {
      void sendVoyadoCartChangeEvent({
        cart,
        market,
      });
    },
    [market],
  );
  const emptyCart = useCallback((cartRef?: string | number) => {
    void sendVoyadoEmptyCartEvent(cartRef);
  }, []);
  const viewCart = useCallback((cart: Cart) => {
    void gTagViewCart(cart);
  }, []);
  const viewItem = useCallback(
    (item: Variant) => {
      void gTagViewItem(item, item, market.currency);
      void sendVoyadoProductViewEvent({
        item,
        market,
      });
    },
    [market],
  );
  const viewList = useCallback(
    (items: (Product | Variant)[], page: Page) => {
      void gTagViewItemList(items, market.currency, page);
    },
    [market],
  );
  const checkoutDenied = useCallback(
    (cart?: Cart | null) => {
      if (!cart) return;
      void gTagCheckoutDenied(cart.id, market.code);
    },
    [market],
  );

  return {
    addPaymentInfo,
    addShippingInfo,
    addToCart,
    beginCheckout,
    clickItem,
    emailRegistration,
    purchase,
    removeFromCart,
    updateCart,
    emptyCart,
    viewCart,
    viewItem,
    viewList,
    checkoutDenied,
  };
}
